import classnames from 'classnames';
import React from 'react';

import { useTheme } from '../../contexts/Theme';
import { CheckIcon } from '../Icons/CheckIcon';

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label?: string | JSX.Element;
  name: string;
}

export const Checkbox: React.FC<React.PropsWithChildren<Props>> = ({
  checked,
  disabled,
  id,
  label = null,
  ...props
}) => {
  const { colors, fontFamilies } = useTheme();
  const isControlled = typeof checked === 'boolean';

  return (
    <div>
      <input
        checked={checked}
        className={classnames({
          isControlled,
          checked,
        })}
        disabled={disabled}
        id={id}
        type="checkbox"
        {...props}
      />
      <label htmlFor={id}>{label}</label>
      <CheckIcon
        className="checkIcon"
        color={disabled ? colors.supportiveText : 'currentColor'}
        size={16}
      />
      <style jsx>{`
        div {
          position: relative;
        }
        input {
          opacity: 0;
          position: absolute;
        }
        label {
          cursor: pointer;
          display: inline-block;
          padding-left: ${label ? '24px' : '16px'};
          -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
        }
        label::before {
          background: var(--input-bg);
          border: 1px solid var(--radio-border);
          border-radius: 4px;
          box-shadow: 0 0 0 0 var(--focus-shadow);
          content: '';
          height: 16px;
          left: 0;
          position: absolute;
          top: 1px;
          transition: border 200ms ease, box-shadow 200ms ease;
          width: 16px;
        }
        input ~ :global(.checkIcon) {
          left: 0;
          opacity: 0;
          pointer-events: none;
          position: absolute;
          top: 1px;
          transition: opacity 200ms ease;
        }
        .isControlled.checked + label,
        input:not(.isControlled):checked + label {
          font-family: ${fontFamilies.medium};
          font-weight: normal;
        }
        .isControlled.checked ~ :global(.checkIcon),
        input:not(.isControlled):checked ~ :global(.checkIcon) {
          opacity: 1;
        }
        input:not([disabled]) + label:hover::before,
        div:focus-within label::before {
          border-color: var(--focus-border);
          box-shadow: 0 0 0 2px var(--focus-shadow);
        }
        input[disabled] + label {
          color: var(--text-muted);
          cursor: default;
        }
        input[disabled] + label::before {
          opacity: 0.6;
        }
      `}</style>
    </div>
  );
};
